import {
  type CampaignCreationTaskSubmissionInput,
  CampaignCreationTaskSubmissionInputSchema,
  type SmartCreationBrowserDraft,
  SmartCreationBrowserDraftSchema,
} from "@adpilot/shared";
import { z } from "zod";
import { useAuthStore } from "@/stores/auth";

export const LocalSmartCreationPlanSchema = z
  .object({
    version: z.literal(2),
    plan: SmartCreationBrowserDraftSchema,
    signature: z.string().regex(/^[a-f0-9]{64}$/),
    signedPlan: SmartCreationBrowserDraftSchema.optional(),
    submission: CampaignCreationTaskSubmissionInputSchema.optional(),
  })
  .strict();
export type LocalSmartCreationPlan = z.infer<typeof LocalSmartCreationPlanSchema>;
const memory = new Map<string, LocalSmartCreationPlan>();
const memoryOnly = new Set<string>();
const serialized = new Map<string, string>();
const cleared = new Set<string>();
const acceptedTaskSchema = z
  .object({ planId: z.string().uuid(), shopId: z.string().uuid(), taskId: z.string().uuid() })
  .strict();
const acceptedTasks = new Map<string, z.infer<typeof acceptedTaskSchema>>();

export function smartCreationOwnerKey(): string {
  const user = useAuthStore.getState().user;
  return `${user?.activeWorkspaceId ?? "none"}:${user?.id ?? "signed-out"}`;
}

function prefix(): string {
  return `adpilot:smart-creation:plans:${smartCreationOwnerKey()}:`;
}
function key(plan: Pick<SmartCreationBrowserDraft, "shopId" | "id">): string {
  return `${prefix()}${plan.shopId}:${plan.id}`;
}

export function readLocalSmartCreationPlan(
  planId?: string,
  shopId?: string,
): LocalSmartCreationPlan | null {
  const ownerPrefix = prefix();
  if (typeof window !== "undefined") {
    try {
      const stored = new Set<string>();
      const storageKeys =
        planId && shopId
          ? [key({ id: planId, shopId })]
          : Array.from({ length: window.localStorage.length }, (_, index) =>
              window.localStorage.key(index),
            );
      for (const storageKey of storageKeys) {
        if (!storageKey?.startsWith(ownerPrefix) || cleared.has(storageKey)) continue;
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) continue;
        if (serialized.get(storageKey) === raw && memory.has(storageKey)) {
          stored.add(storageKey);
          continue;
        }
        let decoded: unknown;
        try {
          decoded = JSON.parse(raw);
        } catch {
          memory.delete(storageKey);
          memoryOnly.delete(storageKey);
          window.localStorage.removeItem(storageKey);
          continue;
        }
        const parsed = LocalSmartCreationPlanSchema.safeParse(decoded);
        if (parsed.success && storageKey === key(parsed.data.plan)) {
          const entry = parsed.data;
          memory.set(storageKey, entry);
          serialized.set(storageKey, raw);
          stored.add(storageKey);
        }
      }
      for (const [storageKey, entry] of memory.entries()) {
        if (
          storageKey.startsWith(ownerPrefix) &&
          (!planId || entry.plan.id === planId) &&
          (!shopId || entry.plan.shopId === shopId) &&
          !stored.has(storageKey) &&
          !memoryOnly.has(storageKey)
        ) {
          memory.delete(storageKey);
          serialized.delete(storageKey);
        }
      }
    } catch {
      // Storage can be unavailable. Keep the current page's draft usable.
    }
  }
  return (
    [...memory.entries()]
      .filter(
        ([storageKey, entry]) =>
          storageKey.startsWith(ownerPrefix) &&
          (!planId || entry.plan.id === planId) &&
          (!shopId || entry.plan.shopId === shopId),
      )
      .map(([, entry]) => entry)
      .sort((left, right) => right.plan.updatedAt.localeCompare(left.plan.updatedAt))[0] ?? null
  );
}

function persistLocalEntry(entry: LocalSmartCreationPlan): boolean {
  const storageKey = key(entry.plan);
  cleared.delete(storageKey);
  memory.set(storageKey, entry);
  try {
    const raw = JSON.stringify(entry);
    window.localStorage.setItem(storageKey, raw);
    serialized.set(storageKey, raw);
    memoryOnly.delete(storageKey);
    return true;
  } catch {
    memoryOnly.add(storageKey);
    return false;
  }
}

export function writeLocalSmartCreationPlan(
  plan: SmartCreationBrowserDraft,
  signature: string,
): boolean {
  return persistLocalEntry(LocalSmartCreationPlanSchema.parse({ version: 2, plan, signature }));
}

/** The signed generation remains immutable while the browser owns editable values. */
export function writeLocalSmartCreationEditedPlan(plan: SmartCreationBrowserDraft): boolean {
  const current = readLocalSmartCreationPlan(plan.id, plan.shopId);
  if (!current) throw new Error("The local Smart Creation draft is missing");
  if (current.submission) throw new Error("The creation submission is awaiting its task result");
  return persistLocalEntry(
    LocalSmartCreationPlanSchema.parse({
      ...current,
      signedPlan: current.signedPlan ?? current.plan,
      plan,
    }),
  );
}

const SignedReviewRevisionSchema = z.object({
  reviewWorkspace: z.object({ workspaceRevision: z.number() }).nullish(),
});

/** Review edits live only in the browser; regenerating from the signed plan discards them. */
export function hasLocalSmartCreationReviewEdits(planId: string, shopId: string): boolean {
  const entry = readLocalSmartCreationPlan(planId, shopId);
  const edited = entry?.plan.reviewWorkspace;
  if (!entry?.signedPlan || !edited) return false;
  const signed = SignedReviewRevisionSchema.safeParse(entry.signedPlan);
  // An unreadable signed generation cannot prove the edits are absent.
  if (!signed.success) return true;
  return edited.workspaceRevision > (signed.data.reviewWorkspace?.workspaceRevision ?? 0);
}

export function prepareSmartCreationSubmission(
  planId: string,
  shopId: string,
  request: CampaignCreationTaskSubmissionInput,
): CampaignCreationTaskSubmissionInput {
  const entry = readLocalSmartCreationPlan(planId, shopId);
  if (!entry) throw new Error("The local Smart Creation draft is missing");
  if (entry.submission) {
    if (!persistLocalEntry(entry)) {
      throw new Error("The creation submission could not be saved in this browser");
    }
    return entry.submission;
  }
  const submission = CampaignCreationTaskSubmissionInputSchema.parse(request);
  if (!persistLocalEntry({ ...entry, submission })) {
    throw new Error("The creation submission could not be saved in this browser");
  }
  return submission;
}

/** Configuration becomes editable only after the API proves that the snapshot was never accepted. */
export function clearRejectedSmartCreationSubmission(
  planId: string,
  shopId: string,
  submissionId: string,
): boolean {
  const entry = readLocalSmartCreationPlan(planId, shopId);
  if (!entry?.submission || entry.submission.submissionId !== submissionId) return false;
  const { submission: _submission, ...editable } = entry;
  if (persistLocalEntry(editable)) return true;
  memory.set(key(entry.plan), entry);
  return false;
}

export function readSmartCreationPendingDraft(submissionId: string): LocalSmartCreationPlan | null {
  readLocalSmartCreationPlan();
  return (
    [...memory.entries()].find(
      ([storageKey, entry]) =>
        storageKey.startsWith(prefix()) && entry.submission?.submissionId === submissionId,
    )?.[1] ?? null
  );
}

function acceptedTaskKey(planId: string) {
  return `adpilot:campaign-creation:receipts:${smartCreationOwnerKey()}:${planId}`;
}

export function rememberSmartCreationAcceptedTask(planId: string, shopId: string, taskId: string) {
  const receipt = acceptedTaskSchema.parse({ planId, shopId, taskId });
  const storageKey = acceptedTaskKey(planId);
  acceptedTasks.set(storageKey, receipt);
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(receipt));
    return true;
  } catch {
    // Retain the pending submission when the accepted receipt cannot be persisted.
    return false;
  }
}

export function readSmartCreationAcceptedTask(planId: string) {
  const storageKey = acceptedTaskKey(planId);
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) {
      const parsed = acceptedTaskSchema.safeParse(JSON.parse(raw));
      if (parsed.success && parsed.data.planId === planId) {
        acceptedTasks.set(storageKey, parsed.data);
        return parsed.data;
      }
    }
  } catch {
    // A receipt still supports navigation while storage is unavailable in this page.
  }
  return acceptedTasks.get(storageKey) ?? null;
}

export function clearLocalSmartCreationPlan(planId: string, shopId: string): boolean {
  const storageKey = key({ id: planId, shopId });
  memory.delete(storageKey);
  serialized.delete(storageKey);
  memoryOnly.delete(storageKey);
  // A committed confirmation must stay successful even if browser cleanup fails.
  // Suppress the old record in this page; warn the user if it may return on reload.
  cleared.add(storageKey);
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
}
